import { envConfig } from "@/config/env";
import type { ApiResponse } from "@/types/api";
export type RequestPath = string | URL | Request;

interface ApiRequestOptions extends RequestInit {
  noJson?: boolean; // Flag to disable JSON Content-Type
}

interface RequestMethodOptions {
  body?: unknown;
  options?: ApiRequestOptions;
}

interface ApiClient {
  get<T>(
    url: RequestPath,
    options?: ApiRequestOptions
  ): Promise<ApiResponse<T>>;
  post<T>(
    url: RequestPath,
    body?: unknown,
    options?: ApiRequestOptions
  ): Promise<ApiResponse<T>>;
  put<T>(
    url: RequestPath,
    body?: unknown,
    options?: ApiRequestOptions
  ): Promise<ApiResponse<T>>;
  delete<T>(
    url: RequestPath,
    options?: ApiRequestOptions
  ): Promise<ApiResponse<T>>;
}

const isRegularResponse = (data: Record<string, unknown>) => {
  return (
    Object.prototype.hasOwnProperty.call(data, "success") &&
    (Object.prototype.hasOwnProperty.call(data, "message") ||
      Object.prototype.hasOwnProperty.call(data, "error")) &&
    Object.prototype.hasOwnProperty.call(data, "data")
  );
};

const isJsonContentType = (headers?: HeadersInit): boolean => {
  if (!headers) return false;

  const headersObj = new Headers(headers);
  const contentType = headersObj.get("Content-Type");
  return contentType?.includes("application/json") ?? false;
};

const getDefaultHeaders = (): HeadersInit => {
  return {
    "Content-Type": "application/json",
  };
};

export function withDefaultError<T>(
  response: ApiResponse<T>,
  defaultMessage: string
): ApiResponse<T> {
  if (!response.ok) {
    return {
      ...response,
      error: response.error || defaultMessage,
    };
  }
  return response;
}

export class HttpResponse {
  static async parseResponse<T>(response: Response): Promise<ApiResponse<T>> {
    try {
      const data: ApiResponse<T> = await response.json();

      const isSuccess = response.ok;

      if (response.status === 503) {
        return {
          data: null,
          ok: false,
          success: false,
          status: 503,
          message: "Serveur inaccessible",
          error: data.error || "Impossible de se connecter au serveur",
        };
      }

      if (!response.ok) {
        return {
          ...data,
          ok: isSuccess,
          success: isSuccess,
          status: response.status,
          error: data.error || data.message || response.statusText,
          message: data.message || response.statusText,
        };
      }

      if (!isRegularResponse(data)) {
        return {
          data: data as T,
          ok: isSuccess,
          success: isSuccess,
          status: response.status,
          error: response.statusText,
          message: data.message || response.statusText,
        };
      }

      return {
        ...data,
        ok: isSuccess,
        success: isSuccess,
        status: response.status,
        error: null,
      };
    } catch (error) {
      return {
        data: null,
        ok: false,
        success: false,
        status: response.status,
        message:
          error instanceof Error ? error.message : "Failed to parse response",
        error:
          error instanceof Error ? error.message : "Failed to parse response",
      };
    }
  }

  static handleNetworkError<T>(error: unknown): ApiResponse<T> {
    return {
      message: "Network error",
      data: null,
      ok: false,
      success: false,
      status: 0,
      error: error instanceof Error ? error.message : "Network error",
    };
  }
}

export function requestBuilder(
  method: string,
  options?: ApiRequestOptions,
  body?: unknown
): ApiRequestOptions {
  const { noJson, ...restOptions } = options || {};

  const requestOptions: RequestInit = {
    ...restOptions,
    method,
  };

  if (!noJson) {
    requestOptions.headers = {
      ...getDefaultHeaders(),
      ...restOptions?.headers,
    };
  } else {
    requestOptions.headers = restOptions?.headers;
  }

  if (body && isJsonContentType(requestOptions.headers)) {
    requestOptions.body = JSON.stringify(body);
  } else if (body && !isJsonContentType(requestOptions.headers)) {
    requestOptions.body = body as BodyInit;
  }

  return requestOptions;
}

export class ApiClientImpl implements ApiClient {
  private baseUrl: string;
  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private buildUrl(path: string): string {
    return `${this.baseUrl}${path}`;
  }

  private async request<T>(
    url: string,
    options: ApiRequestOptions
  ): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(this.buildUrl(url), options);
      return HttpResponse.parseResponse<T>(response);
    } catch (error) {
      return HttpResponse.handleNetworkError<T>(error);
    }
  }

  async get<T>(
    url: string,
    requestMethodOptions?: RequestMethodOptions
  ): Promise<ApiResponse<T>> {
    const requestOptions = requestBuilder("GET", requestMethodOptions?.options);
    return this.request<T>(url, requestOptions);
  }

  async post<T>(
    url: string,
    requestMethodOptions?: RequestMethodOptions
  ): Promise<ApiResponse<T>> {
    const requestOptions = requestBuilder(
      "POST",
      requestMethodOptions?.options,
      requestMethodOptions?.body
    );
    return this.request<T>(url, requestOptions);
  }

  async put<T>(
    url: string,
    requestMethodOptions?: RequestMethodOptions
  ): Promise<ApiResponse<T>> {
    const requestOptions = requestBuilder(
      "PUT",
      requestMethodOptions?.options,
      requestMethodOptions?.body
    );
    return this.request<T>(url, requestOptions);
  }

  async delete<T>(
    url: string,
    requestMethodOptions?: RequestMethodOptions
  ): Promise<ApiResponse<T>> {
    const requestOptions = requestBuilder(
      "DELETE",
      requestMethodOptions?.options
    );
    return this.request<T>(url, requestOptions);
  }
}

const apiClient = new ApiClientImpl(envConfig.apiBaseUrl);

export default apiClient;
